// ===== CUSTOMERS (merged Companies + Contacts) =====
export interface Customer {
  id: string;
  name: string;
  industry: string;
  gstNumber: string;
  address: string;
  accountOwner: string;
  tags: string[];
  contactCount: number;
  dealCount: number;
  totalRevenue: number;
  createdAt: string;
  dealId?: string;
  // ERP Summary (read-only)
  erpSummary: {
    totalRevenue: number;
    outstandingBalance: number;
    creditLimit: number;
    lastOrderDate: string;
  };
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  customerId: string;
  customerName: string;
  avatar?: string;
  createdAt: string;
  owner: string;
  tags: string[];
  description: string;
  address: string;
  linkedIn: string;
  twitter: string;
  lastActivity: string;
}

// ===== STAGE KEYS — fixed canonical identifiers used by all formula code =====
// The display *label* is user-editable; the stageKey is permanent and drives logic.
export type StageKey =
  | "new" | "contacted" | "qualified" | "meeting"
  | "proposal" | "negotiation" | "won" | "converted" | "lost";

export const TERMINAL_STAGE_KEYS: StageKey[] = ["won", "converted", "lost"];
export const STAGE_KEY_ORDER: StageKey[] = [
  "new", "contacted", "qualified", "meeting", "proposal", "negotiation", "won", "converted", "lost",
];

// ===== LEADS =====
export type LeadStage = "New" | "Contacted" | "Qualified" | "Meeting" | "Proposal" | "Negotiation" | "Converted" | "Won" | "Lost";

export interface LeadNote {
  id: string;
  content: string;
  author: string;
  createdAt: string;
}

export interface AssignmentEvent {
  id: string;
  fromUserId: string | null; // null = initial assignment
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  byUserId: string;
  byUserName: string;
  at: string; // ISO datetime
  reason?: string;
  transferEmailAccess: boolean;
}

// 0 = unknown, 1 = weak/partial, 2 = confirmed/strong
export interface BANTScore {
  budget: 0 | 1 | 2;
  authority: 0 | 1 | 2;
  need: 0 | 1 | 2;
  timeline: 0 | 1 | 2;
}

export interface NurtureSchedule {
  scheduledFor: string; // ISO date
  days: 30 | 60 | 90;
  lostReason?: string;
  status: "pending" | "sent" | "cancelled";
  createdAt: string;
}

export type LeadPriority = "Low" | "Medium" | "High" | "Critical";
export type LeadStatus  = "Hot" | "Warm" | "Cold";

export interface LeadContact {
  id: string;
  name: string;
  phone: string;
  email: string;
  position: string;
}

export interface Lead {
  id: string;
  companyName: string;
  phone: string;
  email: string;
  source: string;
  owner: string; // legacy display name — kept in sync with assignedTo
  assignedTo: string; // internal ULID of currently assigned user
  assignedToAuth0UserId?: string | null; // Auth0 sub of assigned user — used for frontend permission checks
  assignmentHistory?: AssignmentEvent[];
  expectedValue: number;
  stage: LeadStage;
  createdAt: string;
  stageEnteredAt?: string; // ISO date — when current stage was entered (for days-in-stage)
  tags: string[];
  nextFollowUp: string;
  notes?: LeadNote[];
  bantScore?: BANTScore;
  nurtureSchedule?: NurtureSchedule;
  lostReason?: string;
  // ── New sales-operation fields ────────────────────────────────
  priority?: LeadPriority;
  leadStatus?: LeadStatus;
  requirementDescription?: string;
  followUpNotes?: string;
  reminderAt?: string;        // ISO datetime
  productService?: string;    // master: product_service
  companySize?: string;       // master: company_size
  attachments?: string[];     // list of URLs / file names
  conversionProbability?: number; // 0-100
  estimatedClosureDate?: string;  // YYYY-MM-DD
  contactPersons?: LeadContact[];
  displayId?: string;             // Human-readable sequential ID, e.g. "LEAD-0001"
}

// ===== DEALS =====
export type DealStage = "New" | "Contacted" | "Qualified" | "Meeting" | "Proposal" | "Negotiation" | "Converted" | "Won" | "Lost";

export const DEAL_STAGE_CHECKLISTS: Record<DealStage, string[]> = {
  New:         ["Confirm contact details", "Log initial discovery call"],
  Contacted:   ["Send introduction email", "Schedule discovery call"],
  Qualified:   ["Verify budget range (BANT)", "Identify decision maker", "Confirm business need"],
  Meeting:     ["Send meeting agenda", "Log meeting notes", "Identify stakeholders"],
  Proposal:    ["Share product demo", "Prepare and send formal proposal", "Confirm pricing approval internally"],
  Negotiation: ["Review legal/contract terms", "Get internal sign-off on discount", "Send revised proposal"],
  Converted:   ["Create customer record", "Hand off to onboarding"],
  Won:         ["Generate order in ERP", "Schedule onboarding call", "Introduce CS team"],
  Lost:        ["Log loss reason", "Schedule nurture follow-up"],
};

export interface StageChecklistItem {
  id?: string;
  stage: DealStage;
  label: string;
  completedAt?: string;
  sortOrder?: number;
}

export type StakeholderRole = "Champion" | "Decision Maker" | "Evaluator" | "Legal" | "Finance" | "Technical" | "Blocker" | "Other";

export interface DealStakeholder {
  id: string;
  contactId?: string;
  name: string;
  role: StakeholderRole;
  email?: string;
  phone?: string;
  notes?: string;
  influence: "High" | "Medium" | "Low";
}

export interface LineItem {
  id: string;
  product: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface OnboardingHandoff {
  scheduledDate?: string;
  csOwner?: string;
  notes?: string;
  completedAt?: string;
}

export interface Deal {
  id: string;
  name?: string;
  companyName: string;
  customerId?: string;
  value: number;
  stage: DealStage;
  probability: number;
  expectedCloseDate: string;
  owner: string;
  createdAt: string;
  aging: number;
  nextActivity: string;
  lastActivityDate: string;
  lossReason?: string;
  lossNotes?: string;
  leadId?: string;
  lineItems?: LineItem[];
  stakeholders?: DealStakeholder[];
  stageChecklist?: StageChecklistItem[];
  forecastCategory?: "Commit" | "Upside" | "Pipeline" | "Omit";
  inheritedNotes?: LeadNote[];
  onboarding?: OnboardingHandoff;
  territory?: string;
}

// ===== ACTIVITIES =====
export type ActivityType = "call" | "meeting" | "follow-up" | "task" | "note" | "email";

export type TaskStatus = "pending" | "in_progress" | "done";

export interface ProgressNote {
  id: string;
  text: string;
  by: string; // user name
  at: string; // ISO datetime
}

export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  contactName: string;
  companyName: string;
  owner: string;
  date: string;
  time: string;
  completed: boolean;
  completedAt?: string;
  priority?: "low" | "medium" | "high";
  relatedType?: "lead" | "deal" | "customer" | "contact";
  relatedTo?: string;
  relatedId?: string;
  createdAt?: string;
  // Task assignment (Phase 7)
  assignedTo?: string; // userId
  assignedBy?: string; // userId
  status?: TaskStatus;
  progressNotes?: ProgressNote[];
}

// ===== CUSTOMER TIMELINE =====
export interface TimelineEvent {
  id: string;
  customerId: string;
  type: "lead_created" | "call" | "meeting" | "email" | "deal_created" | "proposal_sent" | "order_created" | "note";
  title: string;
  description: string;
  date: string;
  user: string;
}

// ===== AUTOMATION RULES =====
export type AutomationTriggerType = 'lead_created' | 'lead_stage_changed' | 'lead_won' | 'lead_lost';
export type AutomationConditionField = 'any' | 'lead_source' | 'lead_priority' | 'lead_stage';
export type AutomationActionType = 'assign_to_user' | 'add_tag' | 'change_stage' | 'create_activity' | 'send_notification' | 'send_email';

export interface AutomationRule {
  id: string;
  name: string;
  triggerType: AutomationTriggerType;
  conditionField: AutomationConditionField;
  conditionValue?: string | null;
  actionType: AutomationActionType;
  actionConfig: Record<string, any>;
  isActive: boolean;
  triggerCount: number;
  lastTriggeredAt?: string | null;
  createdAt?: string;
}

// ===== SMART ALERTS =====
export interface SmartAlert {
  id: string;
  type: "lead_stale" | "deal_stuck" | "customer_inactive";
  title: string;
  description: string;
  severity: "warning" | "alert";
  relatedId: string;
  date: string;
}

// ===== TEAM =====
export type UserStatus = "Active" | "Invited" | "Disabled";

export interface TeamMember {
  id: string;
  authId?: string; // auth0 sub (e.g. "auth0|...") — used to match audit/note records that store the raw sub
  name: string;
  email: string;
  role: string; // role name — can be a built-in or custom role
  avatar?: string;
  deals: number;
  revenue: number;
  status: UserStatus;
  lastActive: string; // ISO date string, empty if never
}

// ===== ROLES & PERMISSIONS =====
export type Permission =
  | "view_dashboard"
  | "view_leads" | "edit_leads" | "delete_leads"
  | "view_deals" | "edit_deals" | "delete_deals"
  | "view_customers" | "edit_customers"
  | "view_contacts" | "edit_contacts"
  | "view_activities" | "edit_activities"
  | "view_reports"
  | "view_automation" | "edit_automation"
  | "view_settings"
  | "manage_users" | "manage_roles" | "manage_master_data";

export interface RoleDefinition {
  name: string;
  color: string; // tailwind/hsl color token, e.g. "hsl(217, 91%, 60%)"
  permissions: Permission[];
  isBuiltIn: boolean;
}

export const defaultRoles: RoleDefinition[] = [
  {
    name: "Admin",
    color: "hsl(0, 84%, 60%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_leads", "edit_leads", "delete_leads",
      "view_deals", "edit_deals", "delete_deals",
      "view_customers", "edit_customers", "view_contacts", "edit_contacts",
      "view_activities", "edit_activities",
      "view_reports", "view_automation", "edit_automation", "view_settings",
      "manage_users", "manage_roles", "manage_master_data",
    ],
  },
  {
    name: "Sales Manager",
    color: "hsl(217, 91%, 60%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_leads", "edit_leads",
      "view_deals", "edit_deals",
      "view_customers", "edit_customers", "view_contacts", "edit_contacts",
      "view_activities", "edit_activities",
      "view_reports", "view_settings",
    ],
  },
  {
    name: "Sales Executive",
    color: "hsl(142, 71%, 45%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_leads", "edit_leads",
      "view_deals", "edit_deals",
      "view_customers", "view_contacts", "edit_contacts",
      "view_activities", "edit_activities", "view_reports",
    ],
  },
  {
    name: "Support Agent",
    color: "hsl(38, 92%, 50%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_customers", "view_contacts",
      "view_activities", "edit_activities",
    ],
  },
  {
    name: "Dealer",
    color: "hsl(280, 65%, 55%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_leads", "edit_leads",
      "view_deals", "view_contacts", "edit_contacts",
      "view_activities", "edit_activities",
    ],
  },
  {
    name: "Partner",
    color: "hsl(195, 85%, 45%)",
    isBuiltIn: true,
    permissions: [
      "view_dashboard", "view_leads", "edit_leads",
      "view_deals", "edit_deals",
      "view_customers", "view_contacts", "edit_contacts",
      "view_activities", "edit_activities", "view_reports",
    ],
  },
];

// ===== TEAMS (manager + reports hierarchy) =====
export interface Team {
  id: string;
  name: string;
  managerId: string; // userId of the manager
  memberIds: string[]; // userIds of executives reporting to this manager
}

export const defaultTeams: Team[] = [
  {
    id: "t1",
    name: "Enterprise Sales",
    managerId: "u1", // Ravi Kumar
    memberIds: ["u2", "u3"], // Priya Sharma, Amit Patel
  },
  {
    id: "t2",
    name: "SMB & Startups",
    managerId: "u1",
    memberIds: ["u4"], // Sneha Reddy
  },
  {
    id: "t3",
    name: "Customer Success",
    managerId: "u7", // Admin User
    memberIds: ["u5", "u6"], // Deepak, Neha
  },
];

// ===== EMAILS =====
export interface EmailAttachment {
  id: string;
  name: string;
  sizeKb: number;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  leadId: string;
  from: string; // email address
  fromName: string;
  to: string[];
  cc?: string[];
  subject: string;
  body: string; // plain text / simple markdown
  direction: "in" | "out";
  sentAt: string; // ISO datetime
  readAt?: string;
  attachments?: EmailAttachment[];
}

export interface EmailThread {
  id: string;
  leadId: string;
  subject: string;
  participants: string[]; // email addresses
  lastMessageAt: string; // ISO
  messageIds: string[]; // ordered oldest -> newest
}

// ===== EMAIL ACCOUNTS (Phase 3 — mock per-user email connection) =====
export type EmailProvider = "gmail" | "outlook" | "imap";
export type EmailAccountStatus = "connected" | "disconnected" | "error";

export interface EmailAccount {
  id: string;
  userId: string; // teamMember.id
  provider: EmailProvider;
  emailAddress: string;
  displayName: string;
  status: EmailAccountStatus;
  lastSyncAt: string; // ISO datetime
  errorMessage?: string;
}

// ===== MASTER MODULE (Phase 6) =====
export interface MasterItem {
  id: string;
  label: string;               // User-visible name — editable
  stageKey?: StageKey;         // Fixed canonical type — formula code binds to this, not the label
  defaultProbability?: number; // Default conversion probability (0-100) for this stage type
  color?: string;
  order?: number;
  active: boolean;
  showInDeals?: boolean;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string; // supports {{lead.companyName}}, {{lead.companyName}}, {{user.name}}
  active: boolean;
}

export const masterLeadSources: MasterItem[] = [
  { id: "ls1", label: "Website", active: true, order: 1 },
  { id: "ls2", label: "Referral", active: true, order: 2 },
  { id: "ls3", label: "LinkedIn", active: true, order: 3 },
  { id: "ls4", label: "Trade Show", active: true, order: 4 },
  { id: "ls5", label: "Cold Call", active: true, order: 5 },
  { id: "ls6", label: "Partner", active: false, order: 6 },
];

export const masterLeadStages: MasterItem[] = [
  { id: "lst1", label: "New",         stageKey: "new",         defaultProbability: 10,  color: "hsl(217, 91%, 60%)", order: 1, active: true },
  { id: "lst2", label: "Contacted",   stageKey: "contacted",   defaultProbability: 20,  color: "hsl(38, 92%, 50%)",  order: 2, active: true },
  { id: "lst3", label: "Qualified",   stageKey: "qualified",   defaultProbability: 35,  color: "hsl(142, 71%, 45%)", order: 3, active: true },
  { id: "lst4", label: "Meeting",     stageKey: "meeting",     defaultProbability: 50,  color: "hsl(260, 65%, 55%)", order: 4, active: true },
  { id: "lst5", label: "Proposal",    stageKey: "proposal",    defaultProbability: 65,  color: "hsl(200, 85%, 50%)", order: 5, active: true },
  { id: "lst6", label: "Negotiation", stageKey: "negotiation", defaultProbability: 80,  color: "hsl(38, 92%, 40%)",  order: 6, active: true },
  { id: "lst7", label: "Won",         stageKey: "won",         defaultProbability: 100, color: "hsl(142, 76%, 36%)", order: 7, active: true, showInDeals: true },
  { id: "lst8", label: "Converted",   stageKey: "converted",   defaultProbability: 100, color: "hsl(280, 65%, 60%)", order: 8, active: true },
  { id: "lst9", label: "Lost",        stageKey: "lost",        defaultProbability: 0,   color: "hsl(0, 84%, 60%)",   order: 9, active: true },
];

export const masterActivityTypes: MasterItem[] = [
  { id: "at1", label: "Call", color: "hsl(217, 91%, 60%)", active: true },
  { id: "at2", label: "Meeting", color: "hsl(38, 92%, 50%)", active: true },
  { id: "at3", label: "Email", color: "hsl(142, 71%, 45%)", active: true },
  { id: "at4", label: "Follow-up", color: "hsl(0, 84%, 60%)", active: true },
  { id: "at5", label: "Task", color: "hsl(280, 65%, 60%)", active: true },
  { id: "at6", label: "Note", color: "hsl(190, 80%, 45%)", active: true },
];

export const masterIndustries: MasterItem[] = [
  { id: "in1", label: "Technology", active: true },
  { id: "in2", label: "Agriculture", active: true },
  { id: "in3", label: "Construction", active: true },
  { id: "in4", label: "Marketing", active: true },
  { id: "in5", label: "Insurance", active: true },
  { id: "in6", label: "Retail", active: true },
  { id: "in7", label: "Healthcare", active: true },
];

export const masterTags: MasterItem[] = [
  { id: "tg1", label: "Hot", color: "hsl(0, 84%, 60%)", active: true },
  { id: "tg2", label: "Warm", color: "hsl(38, 92%, 50%)", active: true },
  { id: "tg3", label: "Cold", color: "hsl(217, 91%, 60%)", active: true },
  { id: "tg4", label: "Enterprise", color: "hsl(280, 65%, 60%)", active: true },
  { id: "tg5", label: "SMB", color: "hsl(142, 71%, 45%)", active: true },
  { id: "tg6", label: "Startup", color: "hsl(190, 80%, 45%)", active: true },
];

export const masterLeadPriorities: MasterItem[] = [
  { id: "lp1", label: "Low", color: "hsl(217, 91%, 60%)", active: true, order: 1 },
  { id: "lp2", label: "Medium", color: "hsl(38, 92%, 50%)", active: true, order: 2 },
  { id: "lp3", label: "High", color: "hsl(142, 71%, 45%)", active: true, order: 3 },
  { id: "lp4", label: "Critical", color: "hsl(0, 84%, 60%)", active: true, order: 4 },
];

export const masterLeadStatuses: MasterItem[] = [
  { id: "lss1", label: "Cold", color: "hsl(217, 91%, 60%)", active: true, order: 1 },
  { id: "lss2", label: "Warm", color: "hsl(38, 92%, 50%)", active: true, order: 2 },
  { id: "lss3", label: "Hot", color: "hsl(0, 84%, 60%)", active: true, order: 3 },
];

export const masterProductServices: MasterItem[] = [
  { id: "ps1", label: "ERP Implementation", active: true, order: 1 },
  { id: "ps2", label: "CRM Setup", active: true, order: 2 },
  { id: "ps3", label: "Cloud Migration", active: true, order: 3 },
  { id: "ps4", label: "Digital Marketing Suite", active: true, order: 4 },
  { id: "ps5", label: "Custom Software", active: true, order: 5 },
  { id: "ps6", label: "IT Support", active: true, order: 6 },
  { id: "ps7", label: "Training & Onboarding", active: true, order: 7 },
  { id: "ps8", label: "Consulting", active: true, order: 8 },
];

export const masterCompanySizes: MasterItem[] = [
  { id: "cs1", label: "1-10", active: true, order: 1 },
  { id: "cs2", label: "11-50", active: true, order: 2 },
  { id: "cs3", label: "51-200", active: true, order: 3 },
  { id: "cs4", label: "201-500", active: true, order: 4 },
  { id: "cs5", label: "501-1000", active: true, order: 5 },
  { id: "cs6", label: "1000+", active: true, order: 6 },
];

export const masterEmailTemplates: EmailTemplate[] = [
  {
    id: "et-tpl-1",
    name: "Initial Outreach",
    subject: "Hello from Febi — quick intro",
    body: "Hi {{lead.companyName}},\n\nI'm {{user.name}} from Febi. We help companies like {{lead.companyName}} streamline their sales process. Would you be open to a 15-minute call this week to explore if there's a fit?\n\nBest,\n{{user.name}}",
    active: true,
  },
  {
    id: "et-tpl-2",
    name: "Proposal Follow-up",
    subject: "Following up on the proposal — {{lead.companyName}}",
    body: "Hi {{lead.companyName}},\n\nJust checking in on the proposal we sent. Happy to walk through any questions or revise scope as needed. Let me know what works best.\n\nThanks,\n{{user.name}}",
    active: true,
  },
  {
    id: "et-tpl-3",
    name: "Demo Confirmation",
    subject: "Demo confirmed — {{lead.companyName}}",
    body: "Hi {{lead.companyName}},\n\nConfirming our demo for next week. I'll share a calendar invite shortly. Looking forward to showing you what we've built for teams like {{lead.companyName}}.\n\nBest,\n{{user.name}}",
    active: true,
  },
];
